import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Order } from "@/api.types.v2";
import { StatusBadge, type StatusTone } from "@/components/status-badge";

function formatCurrency(cents: number | undefined, language: string) {
  if (cents === undefined) return "—";
  return new Intl.NumberFormat(language === "zh" ? "zh-CN" : "en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function hasOrderRefund(order: Order) {
  return Boolean(
    (order.refundSummary && order.refundSummary.settlementState !== "NONE") ||
      order.paymentStatus === "REFUNDED" ||
      order.paymentStatus === "PARTIAL_REFUND"
  );
}

function OrderRefundSummary({ order }: { order: Order }) {
  const { t, i18n } = useTranslation("app");
  if (!hasOrderRefund(order)) return null;
  const summary = order.refundSummary;
  const money = (cents: number) => formatCurrency(cents, i18n.language);
  const cash = summary?.settledCashCents ?? 0;
  const credit = summary?.settledCreditCents ?? 0;
  const hasSettledRefund = cash + credit > 0;
  const isPartial =
    order.paymentStatus === "PARTIAL_REFUND" ||
    (order.totalPaidCents !== undefined &&
      cash + credit < order.totalPaidCents);
  const unsettled =
    summary &&
    ["PENDING", "PROCESSING", "FAILED"].includes(summary.settlementState);
  const outcomeTone =
    summary?.settlementState === "FAILED" ? "danger" : "warning";
  const outcomeKey = hasSettledRefund
    ? `orders.refundSummary.additional.${summary?.settlementState.toLowerCase()}`
    : `orders.refundSummary.${summary?.settlementState.toLowerCase()}`;
  return (
    <>
      {cash > 0 && (
        <StatusBadge tone="secondary" className="max-w-full whitespace-normal!">
          {t(
            isPartial
              ? "orders.refundSummary.partialCash"
              : "orders.refundSummary.cash",
            { amount: money(cash) }
          )}
        </StatusBadge>
      )}
      {credit > 0 &&
        (cash > 0 ? (
          <p className="w-full text-xs font-normal text-default-500">
            {t("orders.refundSummary.credit", { amount: money(credit) })}
          </p>
        ) : (
          <StatusBadge
            tone="secondary"
            className="max-w-full whitespace-normal!"
          >
            {t("orders.refundSummary.credit", { amount: money(credit) })}
          </StatusBadge>
        ))}
      {unsettled &&
        (hasSettledRefund ? (
          <p className="w-full text-xs font-normal text-default-500">
            {t(outcomeKey)}
          </p>
        ) : (
          <StatusBadge
            tone={outcomeTone}
            className="max-w-full whitespace-normal!"
          >
            {t(outcomeKey)}
          </StatusBadge>
        ))}
      {(!summary || summary.settlementState === "NONE") && (
        <StatusBadge tone="default" className="max-w-full whitespace-normal!">
          {t("orders.refundSummary.unavailable")}
        </StatusBadge>
      )}
      {summary?.settlementState === "COMPLETED" && !hasSettledRefund && (
        <StatusBadge tone="default" className="max-w-full whitespace-normal!">
          {t("orders.refundSummary.noCash")}
        </StatusBadge>
      )}
    </>
  );
}

const paymentDisplay: Partial<
  Record<
    NonNullable<Order["paymentStatus"]>,
    { labelKey: string; tone: StatusTone }
  >
> = {
  PAID: { labelKey: "orders.paidLabel", tone: "success" },
  CREDIT_APPROVED: {
    labelKey: "orders.paymentStatus.netTerms",
    tone: "primary",
  },
  VOIDED: { labelKey: "orders.paymentStatus.voided", tone: "default" },
  FAILED: { labelKey: "orders.paymentStatus.failed", tone: "danger" },
};

export function OrderPaymentSummary({
  order,
  showBreakdown = false,
  variant = "default",
}: {
  order: Order;
  showBreakdown?: boolean;
  variant?: "default" | "detail";
}) {
  const { t, i18n } = useTranslation("app");
  const showPaid =
    order.totalPaidCents !== undefined &&
    (order.totalPaidCents > 0 || order.paymentStatus === "PAID");
  const amount = showPaid ? order.totalPaidCents : order.totalCents;
  const formatted = formatCurrency(amount, i18n.language);
  const status = (order.paymentStatus &&
    paymentDisplay[order.paymentStatus]) || {
    labelKey: "orders.paymentStatus.unpaid",
    tone: "default",
  };
  const showOrderTotal =
    variant === "detail" &&
    showPaid &&
    (hasOrderRefund(order) ||
      (order.totalCents != null && order.totalCents !== order.totalPaidCents));
  return (
    <div
      className={`min-w-0 space-y-1 ${variant === "detail" ? "text-right" : ""}`}
    >
      <p className="text-xs font-normal text-default-500">
        {t(
          showPaid
            ? "orders.refundSummary.totalPaid"
            : "orders.refundSummary.orderTotal"
        )}{" "}
        <span>{t("orders.refundSummary.currency")}</span>
      </p>
      <div
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${variant === "detail" ? "justify-end" : ""}`}
      >
        <span
          className={`font-semibold text-default-900 tabular-nums ${variant === "detail" ? "text-2xl" : "text-base"}`}
        >
          {formatted}
        </span>
        {hasOrderRefund(order) ? (
          <OrderRefundSummary order={order} />
        ) : (
          <StatusBadge tone={status.tone}>{t(status.labelKey)}</StatusBadge>
        )}
      </div>
      {showOrderTotal && (
        <p className="flex justify-between gap-3 pt-2 text-xs text-default-500">
          <span>{t("orders.refundSummary.orderTotal")}</span>
          <span>{formatCurrency(order.totalCents, i18n.language)}</span>
        </p>
      )}
      {showBreakdown && <OrderPriceDetails order={order} />}
    </div>
  );
}

export function OrderPriceDetails({ order }: { order: Order }) {
  const { t, i18n } = useTranslation("app");
  const showPaid =
    order.totalPaidCents !== undefined &&
    (order.totalPaidCents > 0 || order.paymentStatus === "PAID");
  const hasPickupBreakdown =
    showPaid &&
    order.separatePickupPaidCents != null &&
    order.separatePickupPaidCents > 0 &&
    order.totalPaidCents != null;
  const hasTaxBreakdown = order.taxCents != null && order.taxCents > 0;
  return (
    <div className="min-w-0 text-right">
      {" "}
      {(hasPickupBreakdown || hasTaxBreakdown) && (
        <details className="group/price text-xs font-normal text-default-500">
          <summary className="flex min-h-8 cursor-pointer list-none items-center justify-end gap-1.5 [&::-webkit-details-marker]:hidden">
            <ChevronRight
              aria-hidden="true"
              className="h-3.5 w-3.5 transition-transform group-open/price:rotate-90"
            />
            {t("orders.refundSummary.priceDetails")}
          </summary>
          <div className="space-y-1 pt-2 text-left">
            {hasTaxBreakdown && (
              <>
                <p className="flex justify-between gap-3">
                  <span>{t("orderDetails.subtotal")}</span>
                  <span>
                    {formatCurrency(
                      order.subtotalCents ?? order.totalCents,
                      i18n.language
                    )}
                  </span>
                </p>
                <p className="flex justify-between gap-3">
                  <span>{t("orderDetails.tax")}</span>
                  <span>{formatCurrency(order.taxCents, i18n.language)}</span>
                </p>
              </>
            )}
            {hasPickupBreakdown &&
              order.totalPaidCents != null &&
              order.separatePickupPaidCents != null && (
                <>
                  <p className="flex justify-between gap-3">
                    <span>{t("orders.refundSummary.orderPayments")}</span>
                    <span>
                      {formatCurrency(
                        order.totalPaidCents - order.separatePickupPaidCents,
                        i18n.language
                      )}
                    </span>
                  </p>
                  <p className="flex justify-between gap-3">
                    <span>{t("orders.refundSummary.pickupPayments")}</span>
                    <span>
                      {formatCurrency(
                        order.separatePickupPaidCents,
                        i18n.language
                      )}
                    </span>
                  </p>
                </>
              )}
          </div>
        </details>
      )}
    </div>
  );
}
